import { Alert, Layout, Tour, TourProps } from 'antd';
import './ReceptionistDashboard.scss';
import ReceptionistDashboardSider from './ReceptionistSider/ReceptionistSider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ContentComponent from './ContentComponent/ContentComponent';
import AIFrontdeskLogo from '@/assets/images/mobile-log.png';
import {
	getLoggedInUser,
	getResellerUserFeaturesGating,
	getResellerUserFeaturesVisibility,
	getSelectedAgent,
	getSelectedAgentId,
	getcurrentPage,
} from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import {
	CONTACT_SUPPORT_EMAIL,
	DESKTOP_LOGO_S3_URL,
	MOBILE_LOGO_S3_URL,
	POSTHOG_FEATURE_FLAGS,
} from 'src/constants';
import { useMutation, useQuery } from '@tanstack/react-query';
import { updateMultipleStepsCompleted, updateStepsCompleted } from '@/api/user';
import {
	setInstructionSteps,
	setCurrentPage,
	updateProvisionedNumber,
	setSelectedAgent,
} from '@/store/account/slice';
import { useDispatch } from 'react-redux';
import posthog from 'posthog-js';
import SubscriptionUpgradeModal from '@/components/SubscriptionUpgradeModal/SubscriptionUpgradeModal';
import { setReseller } from '@/store/reseller/slice';
import { useSearchParams } from 'react-router-dom';
import { getReceptionistReseller, getResellerLogo } from '@/api/unbrandedReceptionist';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import ReceptionistPromo from './ReceptionistPromo';
// import { Onboarding } from './TestItOut/v2/Onboarding';
import ReceptionistSubCancelPromo from './AgentSubCancelPromo';
import { MenuOutlined } from '@mui/icons-material';
import { Theme } from '@/interfaces/IThemeInterface';
import { setAppTheme } from '@/store/app/slice';
import { getResellerData } from '@/store/reseller/selector';
import { useMobileDeviceCheck } from 'src/common/hooks/useMobile';
import ResellerClientSubscriptionUpdateModal from '@/components/ResellerClientSubscriptionUpgradeModal/ResellerClientSubscriptionUpdateModal';
import { getFeatureFlags } from '@/store/app/selector';
import ConfettiExplosion from 'react-confetti-explosion';
import SMSPricingModal from '@/components/SMSPricingModal';
import { getProvisionedNumber } from '@/api/reservedNumbers';
import ResellerTrialBanner from '@/components/ResellerTrialBanner/ResellerTrialBanner';
// import WorkflowIssuesBanner from '@/components/WorkflowEnhance/WorkflowIssuesBanner';
import { getWorkflowAnalysis } from '@/api/workflowAnalysis';
import { setWorkflowIssues } from '@/store/workflowsAnalysis/slice';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { applyTranslation } from '@/utils/translation';
// import { IntegrationBanner } from '@/components/IntegrationBanner/IntegrationBanner';
import { useAgents } from 'src/Hooks/useAgents';
import OnboardingModal from '@/components/OnboardingModal/OnboardingModal';
import { User } from '@/types/TAccounts';
import FloatingAccountBubble from '@/components/FloatingAccountBubble';
import { safeJSONParse } from '@/utils/helper';

const { Content } = Layout;

export const ReceptionistDashboard: React.FC<{ debrand: boolean; handleClose?: () => void }> = ({
	debrand,
	handleClose,
}) => {
	const { isMobile } = useMobileDeviceCheck();
	const [searchParams] = useSearchParams();
	const dispatch = useDispatch();
	const loggedInUser = useAppSelector(getLoggedInUser);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const { isAgentsLoading: isLoading, refetchAgents: refetch } = useAgents();

	const menuRef = useRef<HTMLDivElement>(null);
	const overviewRef = useRef<HTMLElement | null>(null);
	const [currentStep, setCurrentStep] = useState<number>(0);
	const logsRef = useRef<HTMLElement | null>(null);
	const crmRef = useRef<HTMLElement | null>(null);
	const gcalRef = useRef<HTMLDivElement>(null);
	const intakeRef = useRef<HTMLDivElement>(null);
	const apiWorkflowRef = useRef<HTMLDivElement>(null);
	const [openedOnboarding, setOpenedOnboarding] = useState(false);
	const [confetti, setConfetti] = useState(false);
	const [drawerVisible, setDrawerVisible] = useState(false);
	const [provisionedNumber, setProvisionedNumber] = useState<string>('');
	const [completedFeatures, setCompletedFeatures] = useState<number[]>([]);
	const [showOnboardingModal, setShowOnboardingModal] = useState(false);
	const currentReseller = useAppSelector(getResellerData);
	const featureFlags = useAppSelector(getFeatureFlags);
	const registrationFeatureFlagKey = featureFlags.ONBOARDING_REGISTRATION_REVAMP;
	const newRegistrationFlow =
		registrationFeatureFlagKey ===
		POSTHOG_FEATURE_FLAGS.ONBOARDING_REGISTRATION_REVAMP.variants.NEW;

	const isConflictDetetion = useFeatureFlag('conflict-detection');

	const origin = new URLSearchParams(window.location.search).get('origin');

	useEffect(() => {
		if (origin === 'payment-success') {
			setConfetti(true);
			dispatch(setCurrentPage(ReceptionistDashboardSiderItem.GET_STARTED));
			window.history.replaceState({}, '', window.location.pathname);
		}
		if (origin === 'registration-success') {
			setConfetti(true);
			setShowOnboardingModal(true);

			window.history.replaceState({}, '', window.location.pathname);
		}
	}, [origin]);

	useEffect(() => {
		if (!selectedAgent?.number) {
			return;
		}
		let knowledgeBase = {};
		try {
			knowledgeBase =
				typeof selectedAgent?.knowledgeBase === 'string'
					? safeJSONParse(selectedAgent?.knowledgeBase, {})
					: selectedAgent?.knowledgeBase;
		} catch (err) {
			console.error('Error parsing knowledgeBase:', err);
		}

		const hasStructuredData =
			Array.isArray(knowledgeBase) && knowledgeBase.some((item) => 'structuredData' in item);

		// the condition "isNewOnboardingComplete" in selectedAgent is to check if the selectedAgent was created after the new onboarding was introduced
		// this would prevent the onboarding modal from showing up for old agents which are already onboarded
		if (
			selectedAgent &&
			'isNewOnboardingComplete' in selectedAgent &&
			!selectedAgent?.isNewOnboardingComplete &&
			hasStructuredData
		) {
			setShowOnboardingModal(true);
		}
	}, [selectedAgent, selectedAgent?.isNewOnboardingComplete]);

	const openDrawer = () => setDrawerVisible(true);
	const closeDrawer = () => setDrawerVisible(false);
	const [isSmallScreen, setIsSmallScreen] = useState(window.outerWidth < 768);

	// Handle backdrop click to close drawer
	const handleBackdropClick = (e: React.MouseEvent) => {
		// Only close if clicking the backdrop itself, not its children
		if (e.target === e.currentTarget) {
			closeDrawer();
		}
	};

	// Prevent drawer from closing when clicking inside the sidebar
	const handleSidebarClick = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	// Handle keyboard events for drawer
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && drawerVisible) {
				closeDrawer();
			}
		};

		if (drawerVisible) {
			document.addEventListener('keydown', handleKeyDown);
			// Prevent body scroll when drawer is open
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'unset';
		}

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = 'unset';
		};
	}, [drawerVisible]);

	const { data: logoData } = useQuery({
		queryKey: ['reseller-logo'],
		queryFn: () => getResellerLogo({ key: currentReseller.logo as string }),
		enabled: !!(debrand && currentReseller?.logo),
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const resellerLogoUrl = useMemo(() => {
		if (!logoData) {
			return;
		}

		return URL.createObjectURL(logoData);
	}, [logoData]);

	const getProvisionNumberMutation = useMutation({
		mutationKey: ['provisionedNumberApi'],
		mutationFn: getProvisionedNumber,
		onSuccess(data) {
			setProvisionedNumber(data.provisioned_number || '');
			dispatch(updateProvisionedNumber({ provisionedNumber: data.provisioned_number || '' }));
		},
	});

	const { data: workflowAnalysisData } = useQuery({
		queryKey: ['workflow-analysis'],
		queryFn: async () => {
			const data = await getWorkflowAnalysis({ agentId: selectedAgentId as string });
			return data;
		},
	});

	useEffect(() => {
		if (workflowAnalysisData) {
			dispatch(
				setWorkflowIssues({
					conflicts: workflowAnalysisData.conflicts || [],
					improvements: workflowAnalysisData.improvements || [],
				}),
			);
		}
	}, [workflowAnalysisData]);

	useEffect(() => {
		getProvisionNumberMutation.mutate(selectedAgentId as string);
	}, [selectedAgentId]);

	const logo = (
		<div className={''}>
			{debrand ? (
				resellerLogoUrl ? (
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<img height={80} width={80} src={resellerLogoUrl} alt="RLU RED" />
					</div>
				) : (
					<h1> {currentReseller && currentReseller.businessName} </h1>
				)
			) : isMobile ? (
				<img src={MOBILE_LOGO_S3_URL} alt="ML RED" height={80} width={80} />
			) : (
				<img src={DESKTOP_LOGO_S3_URL} alt="DL RED" />
			)}
		</div>
	);

	useEffect(() => {
		const handleResize = () => {
			setIsSmallScreen(window.outerWidth < 768);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const currentPage = useAppSelector(getcurrentPage);

	const [open, setOpen] = useState(false);

	const handleMenuSelect = (key: ReceptionistDashboardSiderItem) => {
		dispatch(setCurrentPage(key));
	};

	const { data } = useQuery({
		queryKey: ['getResellerInfoByReceptionist'],
		queryFn: () => getReceptionistReseller({ resellerId: loggedInUser.resellerId || '' }),
		enabled: !!(debrand && loggedInUser.resellerClient),
	});

	useEffect(() => {
		if (data) {
			if (currentReseller.emailAddress) {
				return;
			} else {
				dispatch(setReseller(data.reseller));
			}

			if (typeof window !== 'undefined' && data?.reseller?.reseller_name) {
				try {
					const title = debrand ? `${data?.reseller.reseller_name} | Login` : 'Frontdesk | Login';

					// Update the document title (tab title)
					document.title = title;

					// Update the meta description
					const metaDescription = document.querySelector('meta[name="description"]');
					if (metaDescription) {
						metaDescription.setAttribute('content', title);
					}

					// If you still need to update an element with id 'page-title'
					const pageTitle = document.getElementById('page-title');
					if (pageTitle) {
						pageTitle.textContent = title;
					}
				} catch (err) {
					console.error('Error updating page metadata:', err);
				}
			}
		}
	}, [data, debrand]);

	const clientHiddenFeatures = useAppSelector(getResellerUserFeaturesVisibility) || [];
	const clientFeaturesGating = useAppSelector(getResellerUserFeaturesGating) || [];

	useEffect(() => {
		posthog.capture('dashboard_loaded');
		if (clientHiddenFeatures.length > 0 || clientFeaturesGating.length > 0) {
			return;
		}
	}, [searchParams]);

	const getOverviewTarget = useCallback(
		() => overviewRef.current || document.createElement('div'),
		[overviewRef],
	);
	const getLogsTarget = useCallback(
		() => logsRef.current || document.createElement('div'),
		[logsRef],
	);
	const getCrmTarget = useCallback(() => crmRef.current || document.createElement('div'), [crmRef]);

	const newSteps: TourProps['steps'] = [
		{
			title: 'Welcome to My AI Front Desk!',
			description:
				'Our AI Receptionist will bring you more customers. Answer the phone 24/7. Never miss a call again.',
			cover: (
				<img
					alt="tour.png"
					src={AIFrontdeskLogo}
					style={{ width: '25%', height: '25%', flex: 1, margin: 'auto' }}
				/>
			),
		},
		{
			title: 'Tweak it!',
			description: "Edit your Receptionist's settings here. It's easy!",
			target: getOverviewTarget,
		},
		{
			title: 'View Logs',
			description: 'All your logs, in one place.',
			target: getLogsTarget,
		},
		{
			title: 'CRM',
			description: 'Manage, process and collect leads with the power of AI.',
			target: getCrmTarget,
		},
		{
			title: '🎉 Tutorial Complete',
			description: `For help, email ${CONTACT_SUPPORT_EMAIL}. This concludes the tutorial. Start by exploring your dashboard.`,
			cover: (
				<img
					alt="tour.png"
					src={AIFrontdeskLogo}
					style={{ width: '25%', height: '25%', flex: 1, margin: 'auto' }}
				/>
			),
		},
	];

	// First, let's create a helper function to apply theme
	const applyTheme = (theme: Partial<Theme>) => {
		dispatch(setAppTheme(theme));
	};

	useEffect(() => {
		const storedTheme = localStorage.getItem('dashboardTheme');
		try {
			if (storedTheme && storedTheme !== 'undefined') {
				applyTheme(JSON.parse(storedTheme) as Theme);
			}
		} catch (err) {
			console.error('Error parsing storedTheme:', err);
		}

		// Update theme based on currentReseller data
		if (currentReseller) {
			if (Object.keys(currentReseller.dashboardTheme || {}).length > 0) {
				// Set theme from currentReseller's dashboardTheme
				applyTheme(currentReseller.dashboardTheme as Theme);
				// Store theme in localStorage
				localStorage.setItem('dashboardTheme', JSON.stringify(currentReseller.dashboardTheme));
			} else if (currentReseller.theme_color) {
				const theme = { buttonSuccessColor: currentReseller.theme_color };
				applyTheme(theme);
				// Store theme in localStorage
				localStorage.setItem('dashboardTheme', JSON.stringify(theme));
			}
		}
	}, [currentReseller]);

	const { mutate } = useMutation({
		mutationKey: ['updateStepsDone'],
		mutationFn: updateStepsCompleted,
		onSuccess(data: { success: boolean; updated_instruction_steps: number[] }) {
			refetch();
			setOpen(false);
			dispatch(setInstructionSteps({ instructionSteps: data.updated_instruction_steps }));
		},
	});
	const MultipleStepsUpdateMutation = useMutation({
		mutationKey: ['updateMultipleStepsCompleted'],
		mutationFn: updateMultipleStepsCompleted,
		onSuccess(data: { success: boolean; updated_instruction_steps: number[] }) {
			refetch();
			setOpen(false);
			dispatch(setInstructionSteps({ instructionSteps: data.updated_instruction_steps }));
		},
	});

	const onTourClose = () => {
		setOpen(false);
		switch (newRegistrationFlow) {
			case true:
				if (!((selectedAgent?.instruction_steps?.length || 0) > 2) && !(currentStep > 2)) {
					MultipleStepsUpdateMutation.mutate({ steps: [1], agentId: selectedAgentId as string });
					setCurrentStep(2);
				}
				break;

			default:
				setCurrentStep(4);
				if (!selectedAgent?.instruction_steps?.includes(3)) {
					mutate({ stepNumber: 3, agentId: selectedAgentId as string });
				}
				setOpenedOnboarding(true);
		}
	};

	useEffect(() => {
		if (
			newRegistrationFlow &&
			selectedAgent?.number &&
			getProvisionNumberMutation.status !== 'pending'
		) {
			if (searchParams.get('tab') === 'app-integrations') {
				return;
			}
			if (loggedInUser?.inboundDemo || !provisionedNumber) {
				if (loggedInUser?.resellerClient) {
					if (
						!clientFeaturesGating.includes(ReceptionistDashboardSiderItem.GET_STARTED) &&
						!clientHiddenFeatures.includes(ReceptionistDashboardSiderItem.GET_STARTED)
					) {
						dispatch(setCurrentPage(ReceptionistDashboardSiderItem.GET_STARTED));
					}
				} else {
					if (selectedAgent?.pendingTesting || !selectedAgent?.onboardingCompleted) {
						dispatch(setCurrentPage(ReceptionistDashboardSiderItem.GET_STARTED));
					} else {
						dispatch(setCurrentPage(ReceptionistDashboardSiderItem.RECEPTIONIST));
					}
				}
			}
		}
	}, [
		selectedAgent?.number,
		loggedInUser.resellerClient,
		loggedInUser.inboundDemo,
		newRegistrationFlow,
		provisionedNumber,
		getProvisionNumberMutation.status,
	]);

	const baseClass = 'app-dashboard';

	const htmlElement = document.querySelector('html');
	const currentLang = htmlElement?.lang || 'en';

	// Add useEffect to check and update browser language
	useEffect(() => {
		const updateBrowserLanguage = async () => {
			if (loggedInUser?.dashboardLanguage) {
				try {
					// Check for Google Translate cookie
					const match = document.cookie.match(/(^|;)\s*googtrans=([^;]+)/);
					if (match) {
						const langPair = decodeURIComponent(match[2]);
						const targetLang = langPair.split('/')[2];
						if (targetLang === loggedInUser.dashboardLanguage) {
							return; // already in correct language, no need to change
						}
					}

					// If not in correct language, apply translation
					if (currentLang !== loggedInUser.dashboardLanguage) {
						await applyTranslation(loggedInUser.dashboardLanguage);
						document.documentElement.lang = loggedInUser.dashboardLanguage;
					}
				} catch (error) {
					console.error('Error updating browser language:', error);
				}
			} else if (currentLang !== 'en') {
				await applyTranslation('en');
				document.documentElement.lang = 'en';
			}
		};

		if (!isLoading) {
			updateBrowserLanguage();
		}
	}, [loggedInUser?.dashboardLanguage, isLoading, currentLang]);

	const handleOnboardingComplete = () => {
		setShowOnboardingModal(false);
		dispatch(
			setSelectedAgent({ user: { ...selectedAgent, isNewOnboardingComplete: true } as User }),
		);
	};

	return (
		<div className={baseClass}>
			{/* I am adding this sider component to handle navigation on mobile devices , its a dummy component not meant to be changed or eliminated */}
			<ReceptionistDashboardSider
				activeMenuItem={currentPage}
				onMenuSelect={handleMenuSelect}
				className={`${baseClass}__menu-icon`}
				menuRef={menuRef}
				overviewRef={overviewRef}
				logsRef={logsRef}
				crmRef={crmRef}
				debrand={debrand}
				openedOnboarding={openedOnboarding}
				setOpenedOnboarding={setOpenedOnboarding}
				tourOpen={open}
				hidden={true}
			/>
			{confetti && (
				<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
					<ConfettiExplosion force={0.8} duration={2200} particleCount={100} width={1600} />
				</div>
			)}

			<Layout
				className={`${baseClass}__layout`}
				style={{
					height: '100vh',
				}}
			>
				{isSmallScreen && (
					<div
						className={`flex items-center justify-between bg-white p-4 transition-all duration-300 ${drawerVisible ? 'blur-sm pointer-events-none' : ''}`}
					>
						{logo}
						<div className="sm:block w-fit !bg-gray-100 !rounded-2xl shadow-sm p-4 sm:p-6 cursor-pointer hover:bg-gray-200 transition-all duration-300 border border-gray-200">
							<FloatingAccountBubble debrand={debrand} />
						</div>
						<MenuOutlined onClick={openDrawer} />
					</div>
				)}

				{!isSmallScreen && (
					<ReceptionistDashboardSider
						activeMenuItem={currentPage}
						onMenuSelect={handleMenuSelect}
						className={`${baseClass}__menu-icon`}
						menuRef={menuRef}
						overviewRef={overviewRef}
						logsRef={logsRef}
						crmRef={crmRef}
						debrand={debrand}
						openedOnboarding={openedOnboarding}
						setOpenedOnboarding={setOpenedOnboarding}
						tourOpen={open}
					/>
				)}

				{isSmallScreen && drawerVisible && (
					<div
						className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40"
						onClick={handleBackdropClick}
					>
						<div
							className="fixed left-0 top-0 h-full w-64 bg-white shadow-lg z-50"
							onClick={handleSidebarClick}
						>
							<ReceptionistDashboardSider
								activeMenuItem={currentPage}
								onMenuSelect={(key) => {
									handleMenuSelect(key);
									closeDrawer();
								}}
								className={`${baseClass}__menu-icon`}
								menuRef={menuRef}
								overviewRef={overviewRef}
								logsRef={logsRef}
								crmRef={crmRef}
								debrand={debrand}
								openedOnboarding={openedOnboarding}
								setOpenedOnboarding={setOpenedOnboarding}
								tourOpen={open}
							/>
						</div>
					</div>
				)}

				<Layout
					style={{
						width: '100%', // Content takes full width
						flex: 1,
						overflow: 'auto',
					}}
					className={`transition-all duration-300 ${drawerVisible ? 'blur-sm pointer-events-none' : ''}`}
				>
					<Content>
						{!debrand ? (
							<>
								{(selectedAgent?.totalCalls ?? 0) < 10 && (
									<>
										{loggedInUser?.inboundDemo &&
										!loggedInUser?.resellerClient &&
										currentPage !== ReceptionistDashboardSiderItem.GET_STARTED
											? null
											: loggedInUser?.resellerClient && (
													<Alert
														className={`${baseClass}__promo sticky top-0`}
														description={<ReceptionistPromo />}
														closable={true}
													/>
												)}
									</>
								)}

								{isConflictDetetion && (
									<div className="sticky top-0 z-50 max-h-screen">
										{/* <WorkflowIssuesBanner /> */}
									</div>
								)}

								{!loggedInUser?.inboundDemo && (
									<div className="sticky top-0 z-20 max-h-screen">
										{/* <IntegrationBanner /> */}
									</div>
								)}
								<ReceptionistSubCancelPromo />
							</>
						) : (
							loggedInUser?.resellerClient &&
							loggedInUser?.inboundDemo && (
								<>
									<ResellerTrialBanner />
								</>
							)
						)}
						<ContentComponent
							currentPage={currentPage}
							setOpened={setOpen}
							debrand={debrand}
							gcalRef={gcalRef}
							intakeRef={intakeRef}
							apiWorkflowRef={apiWorkflowRef}
							setOpenedOnboarding={setOpenedOnboarding}
							currentStep={currentStep}
							setCurrentStep={setCurrentStep}
							openDrawer={openDrawer}
							completedFeatures={completedFeatures}
							setCompletedFeatures={setCompletedFeatures}
							isReceptionistLoading={isLoading}
							loggedInUser={loggedInUser}
						/>
					</Content>
				</Layout>
			</Layout>
			{!loggedInUser?.resellerClient && (
				<>
					<Tour open={open} onClose={onTourClose} steps={newSteps} closeIcon={false} />
				</>
			)}

			<SubscriptionUpgradeModal />
			<ResellerClientSubscriptionUpdateModal />
			<SMSPricingModal />
			<OnboardingModal
				visible={showOnboardingModal}
				onComplete={handleOnboardingComplete}
				handleClose={() => {
					handleClose?.();
				}}
			/>
		</div>
	);
};
